import { apiErrorField } from '@agos/api-client'
import { usePutSettingsPermissionsEmployeeId } from '@agos/api-client/auth'
import { formatCount, T, tt, useAuth } from '@agos/shared'
import { BUTTON_PRIMARY, cn, FilterBar, useToast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  cycleOverride,
  defeatedDenials,
  displayParentOf,
  groupCatalogue,
  heldBecause,
  matchesSearch,
  overrideDiff,
  overridePayload,
  overridesAreDirty,
  overrideState,
  permissionDescription,
  permissionLabel,
  withOverride,
  type EmployeePermissions,
  type Override,
  type OverrideState,
} from '../permission-matrix'

/** §9.152 - how much of the catalogue the exceptions editor is showing. */
type OverrideScope = 'exceptions' | 'granted' | 'all'

/**
 * One employee's exceptions - the old *Výnimky* tab's editor, **moved to the person panel unchanged** (`FRONTEND.md`
 * §9.752). Its markers are the ones `permissions.spec.ts` records: `data-override-diff`, `data-override-dirty`,
 * `data-override-scope`, `data-override-group`, `data-override-row`, `data-held-because`, `data-override-state`,
 * `data-defeated-denials`.
 *
 * Two things moved with it and are not new: *held* now reads the server's `allowed` where the server sends it (the
 * reason is still derived only for the label - `heldBecause`), and the count of rights in force is that same set.
 * Exceptions stay the main administrator's (owner question #165), and `PUT /settings/permissions/employee/{id}` is
 * unchanged.
 */
export function OverridesEditor({
  selected,
  roleName,
  catalogue,
  readFailed,
}: {
  selected: EmployeePermissions
  /** The person's role as drawn - a custom role's own name, which `systemRole` (its base) would hide. */
  roleName: string
  catalogue: readonly string[]
  /** The exceptions read failed - the save is refused on it, whatever else is on screen (§9.543). */
  readFailed: boolean
}) {
  const { refreshPermissions } = useAuth()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const known = useMemo(() => new Set(catalogue), [catalogue])
  const [local, setLocal] = useState<Override[] | null>(null)
  const [search, setSearch] = useState('')
  /**
   * §9.214 - which groups are open, exactly as the roles editor holds them. Collapsed is the default, so the set holds
   * the exceptions rather than the rule: an empty set is the screen as it first draws.
   */
  const [openedGroups, setOpenedGroups] = useState<ReadonlySet<string>>(new Set())
  const groups = useMemo(() => groupCatalogue(catalogue), [catalogue])
  /**
   * §9.152 - which rows are worth looking at. **The default is *Všetko***: narrowing hides every inheriting row, so the
   * one thing you could not do from the default view is *add* an exception - the flow the owner called hard. The
   * editor is keyed by the person, so a different person opens on *Všetko* again.
   */
  const [scope, setScope] = useState<OverrideScope>('all')

  const overrides = local ?? selected.overrides ?? []
  const exceptionCount = catalogue.filter((permission) => overrideState(permission, overrides) !== 'inherit').length
  const denied = overrides.filter((override) => override.granted !== true).map((override) => override.permission ?? '')
  const roleGranted = new Set(selected.rolePermissions ?? [])
  const defeated = defeatedDenials(denied, roleGranted, catalogue)
  const dirty = local !== null
  // The roles editor has had a preview since it was ported; this is where a mistake is quieter - an exception applies
  // to one person and nobody else can see it is there.
  const diff = overrideDiff(selected.overrides ?? [], overrides, catalogue)
  const inForce = selected.allowed ?? selected.effectivePermissions ?? []

  const save = usePutSettingsPermissionsEmployeeId({
    mutation: {
      onSuccess: () => {
        setLocal(null)
        void queryClient.invalidateQueries()
        // An exception is per employee, so it only moves this session when it is this employee's.
        void refreshPermissions()
        toast({ title: tt('Výnimky uložené') })
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: apiErrorField(error, 'error') ?? tt('Výnimky sa neuložili.'),
          variant: 'destructive',
        }),
    },
  })

  return (
    <section className="space-y-3" data-overrides-editor={selected.id}>
      {overridesAreDirty(diff) && (
        <div className="sticky top-0 z-10 rounded-xl border-2 border-primary/40 bg-primary/5 p-3 space-y-1.5" data-override-diff>
          {diff.granted.length > 0 && (
            <p className="text-xs text-emerald-500" data-override-granted>
              + {diff.granted.join(', ')} - <T>povolené len tomuto zamestnancovi</T>
            </p>
          )}
          {diff.denied.length > 0 && (
            <p className="text-xs text-red-500" data-override-denied>
              − {diff.denied.join(', ')} - <T>zakázané len tomuto zamestnancovi</T>
            </p>
          )}
          {/* Returning to the role is not the same as denying, and undoing one is not undoing the other - so it is
              counted apart rather than folded into the removals. */}
          {diff.returnedToRole.length > 0 && (
            <p className="text-xs text-amber-500" data-override-returned>
              ↺ {diff.returnedToRole.join(', ')} - <T>späť podľa roly</T>
            </p>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-4 space-y-1">
        <h3 className="text-sm font-bold">{tt('Výnimky')}</h3>
        <p className="text-xs text-muted-foreground">
          <T>Rola</T> {roleName} · {formatCount(inForce.length)} <T>práv v platnosti</T> ·{' '}
          {overrides.length} <T>výnimiek</T>
        </p>
        <p className="text-[11px] text-muted-foreground">
          <T>Výnimka platí navyše k role: udelenie pridá právo, odobranie ho vezme. Bez výnimky rozhoduje rola.</T>
        </p>
      </div>

      {/* FB-103's second direction, said where it could bite. */}
      {defeated.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-red-500" data-defeated-denials>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Toto odobranie nezaberie - právo je dostupné aj cez</T>{' '}
            {defeated.map((item) => `${item.permission} (${item.reachableVia.join(', ')})`).join(' · ')}.
          </span>
        </p>
      )}

      {dirty && (
        <div className="flex items-center gap-2 rounded-xl border-2 border-primary/40 bg-primary/5 p-3" data-override-dirty>
          <p className="text-xs font-bold flex-1">
            {overrides.length} <T>výnimiek pripravených</T>
          </p>
          <button
            type="button"
            onClick={() => setLocal(null)}
            className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
          >
            <T>Zahodiť</T>
          </button>
          <button
            type="button"
            onClick={() =>
              /* FB-822, §9.543 - stated rather than left to an accident: a failed exceptions read leaves nobody to
                 select, which stopped this by coincidence; the condition says so now. */
              !readFailed &&
              selected.id !== undefined &&
              save.mutate({
                id: selected.id,
                data: overridePayload(overrides, catalogue),
              })
            }
            disabled={save.isPending}
            className={cn('px-3 py-1.5 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
          >
            <T>Uložiť</T>
          </button>
        </div>
      )}

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať právo...') }}
        actions={
          /* §9.152 - the owner's *"much harder … for specific user"*. Four exceptions in 128 rows cannot be found by
             reading, and the count is on the button so it is discoverable without being the default. */
          <div className="flex items-center gap-1" data-override-scope>
            {(
              [
                ['exceptions', `${tt('Len výnimky')}${exceptionCount > 0 ? ` (${exceptionCount})` : ''}`],
                ['granted', tt('Len udelené')],
                ['all', tt('Všetko')],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setScope(id)}
                data-scope={id}
                data-scope-active={scope === id}
                className={cn(
                  'px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-colors',
                  scope === id
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />

      {/* §9.214 - the grouping the roles editor has had since §9.176. */}
      <div className="space-y-2" data-override-rows>
        {groups.map((group) => {
          const rows = group.entries
            .map((entry) => entry.permission)
            .filter((permission) => {
              const state = overrideState(permission, overrides)
              const because = heldBecause(permission, { ...selected, overrides }, known)
              if (scope === 'exceptions' && state === 'inherit') return false
              if (scope === 'granted' && because === 'no') return false
              return true
            })
            .filter((permission) =>
              matchesSearch(permission, permissionLabel(permission, displayParentOf(permission, known)), search),
            )
          if (rows.length === 0) return null

          // This editor exists for the rows that depart from the role, so that is what a collapsed heading counts.
          const exceptions = rows.filter((permission) => overrideState(permission, overrides) !== 'inherit').length
          // §9.176's rule, inherited: a typed search opens every group that still has a row.
          const isOpen = search.trim() !== '' || openedGroups.has(group.key)

          return (
            <div
              key={group.key}
              className="rounded-2xl border border-border bg-card p-3 space-y-1"
              data-override-group={group.key}
            >
              <button
                type="button"
                data-override-group-toggle={group.key}
                aria-expanded={isOpen}
                onClick={() =>
                  setOpenedGroups((current) => {
                    const next = new Set(current)
                    if (next.has(group.key)) next.delete(group.key)
                    else next.add(group.key)
                    return next
                  })
                }
                className="flex items-center gap-2 w-full text-left"
              >
                <ChevronRight
                  className={cn('w-4 h-4 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-90')}
                />
                <h3 className="text-sm font-bold truncate flex-1 min-w-0">{group.heading}</h3>
                <span className="text-[11px] text-muted-foreground tabular-nums shrink-0" data-override-group-count={group.key}>
                  {exceptions > 0 ? (
                    <span className="text-amber-500 font-bold">
                      {exceptions} {tt('výnimiek')}
                    </span>
                  ) : (
                    `${rows.length}`
                  )}
                </span>
              </button>

              {isOpen &&
                rows.map((permission) => {
                  const state = overrideState(permission, overrides)
                  const because = heldBecause(permission, { ...selected, overrides }, known)
                  const parent = displayParentOf(permission, known)
                  const description = permissionDescription(permission)
                  return (
                    <div
                      key={permission}
                      className="flex items-center gap-3 rounded-lg border border-border px-3 py-1.5"
                      data-override-row={permission}
                    >
                      <span className="min-w-0 flex-1">
                        {/* §9.152 - the §9.145 name. */}
                        <span className="block text-xs font-semibold truncate">{permissionLabel(permission, parent)}</span>
                        {description !== null && (
                          <span className="block text-[10px] text-muted-foreground truncate">{description}</span>
                        )}
                        <span className="block text-[10px] text-muted-foreground font-mono truncate">{permission}</span>
                      </span>
                      <span className="text-[10px] text-muted-foreground shrink-0" data-held-because={because}>
                        {because === 'role' && <T>z roly</T>}
                        {because === 'parent' && <T>cez nadradené</T>}
                        {because === 'override' && <T>výnimkou</T>}
                        {because === 'administrator-pass' && <T>cez vstavaného Administrátora</T>}
                        {because === 'no' && <T>nemá</T>}
                      </span>
                      <button
                        type="button"
                        onClick={() => setLocal(withOverride(overrides, permission, cycleOverride(state)))}
                        aria-label={`${permission} - ${overrideLabel(state)}`}
                        data-override-state={state}
                        className={cn(
                          'px-2 py-1 rounded-lg border text-[10px] font-bold shrink-0 w-20',
                          state === 'grant' && 'border-emerald-500/50 text-emerald-500',
                          state === 'deny' && 'border-red-500/50 text-red-500',
                          state === 'inherit' && 'border-border text-muted-foreground',
                        )}
                      >
                        {overrideLabel(state)}
                      </button>
                    </div>
                  )
                })}
            </div>
          )
        })}
      </div>
    </section>
  )
}

function overrideLabel(state: OverrideState): string {
  if (state === 'grant') return tt('udeliť')
  if (state === 'deny') return tt('odobrať')
  return tt('podľa roly')
}
